import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { ui } from '../ui';
import { formatDateTimeET, formatDurationSeconds } from '../format';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { CategoryMultiSelect } from './insights/CategoryMultiSelect';
import { TranscriptViewer } from './TranscriptViewer';
import {
  Category, TranscriptFilter, TranscriptPreview, TranscriptFetchRun, TranscriptCallSummary,
  getTranscriptPreview, runTranscriptFetch, getTranscriptFetchStatus, getTranscriptCalls,
  exportTranscripts,
} from '../api';

interface TranscriptRetrievalPanelProps {
  categories: Category[];
}

// FR-2.2 — the UI's default/first-use filter values exactly reproduce the 829-call analysis
// this feature was scoped against: CategoryID 1 ("Schedule Replacement Class"),
// 2026-08-02..2026-09-12, answered/solo-category/has-agent all true. These are deliberately
// literal, not derived from "today" — the whole point is reproducing a known, already-analyzed
// set on first load.
export const DEFAULT_TRANSCRIPT_FILTER: TranscriptFilter = {
  categoryIds: [1],
  from: '2026-08-02',
  to: '2026-09-12',
  answeredOnly: true,
  soloCategoryOnly: true,
  requireAgent: true,
};

// How often to poll .../fetch/status while a run is in progress server-side — matches
// ZoomSweepPanel's backfill polling interval/pattern exactly.
const POLL_MS = 5_000;

const RESULTS_PAGE_SIZE = 25;

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
};

// Renders one fetch run's six outcome counts. Blocked gets an explicit explanatory line (FR-1.3)
// so a scope-gap run never reads as an ambiguous generic failure.
const TranscriptRunSummary: React.FC<{ run: TranscriptFetchRun }> = ({ run }) => (
  <div className="rounded-md border border-border-default bg-surface p-4 text-sm">
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <span className={run.Status === 'Running' ? 'animate-pulse' : undefined}>
        <StatusBadge status={run.Status} />
      </span>
      <span className="text-muted">Started {formatDateTimeET(run.StartedUtc)}</span>
      {run.FinishedUtc && <span className="text-muted">&middot; Finished {formatDateTimeET(run.FinishedUtc)}</span>}
    </div>
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-secondary">
      <span>Targeted: <span className="font-medium text-primary">{run.Targeted}</span></span>
      <span>Fetched: <span className="font-medium text-primary">{run.Fetched}</span></span>
      <span>No recording: <span className="font-medium text-primary">{run.NoRecording}</span></span>
      <span>No transcript available: <span className="font-medium text-primary">{run.NoTranscriptAvailable}</span></span>
      <span>Blocked: <span className={`font-medium ${run.Blocked > 0 ? 'text-amber-600' : 'text-primary'}`}>{run.Blocked}</span></span>
      <span>Failed: <span className={`font-medium ${run.Failed > 0 ? 'text-red-600' : 'text-primary'}`}>{run.Failed}</span></span>
    </div>
    {run.Status === 'Blocked' && (
      <p className="mt-2 text-amber-700 dark:text-amber-400">
        Blocked by Zoom — the transcript scope (phone:read:recording_transcript) is missing or was
        revoked. This is a distinct, fixable access problem, not a generic run failure.
      </p>
    )}
    {run.ErrorSummary && (
      <p className="mt-2 break-words text-amber-700 dark:text-amber-400">{run.ErrorSummary}</p>
    )}
  </div>
);

// Communication → Admin → Settings → Transcript Retrieval (CommunicationAdmin-only; the whole
// Settings tab is already admin-gated by AdminPage). FR Groups 2/5/6 of
// docs/requirements/call-transcript-retrieval.md: a reusable filter, an admin-triggered async
// fetch run (mirroring ZoomSweepPanel's Run Historical Backfill), a results list with a
// read-only spot-check viewer (OQ-5 resolved in favor of a minimal v1 viewer), and JSON/CSV
// export. Transcripts carry real names/PII (Data Caveats) — every affordance here stays
// CommunicationAdmin-only and is built as a working spot-check tool, not a showcase.
export const TranscriptRetrievalPanel: React.FC<TranscriptRetrievalPanelProps> = ({ categories }) => {
  const [filter, setFilter] = useState<TranscriptFilter>(DEFAULT_TRANSCRIPT_FILTER);

  // ── Preview (FR-2.3 — same filter as fetch/export, so counts always agree) ────────────────
  const [preview, setPreview] = useState<TranscriptPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const loadPreview = useCallback(() => {
    setPreviewLoading(true);
    setPreviewError(null);
    return getTranscriptPreview(filter)
      .then(setPreview)
      .catch(() => setPreviewError('Could not load the preview — try again.'))
      .finally(() => setPreviewLoading(false));
  }, [filter]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  // ── Results list (FR Group 6 / spot-check view) ────────────────────────────────────────────
  const [calls, setCalls] = useState<TranscriptCallSummary[]>([]);
  const [callsLoading, setCallsLoading] = useState(true);
  const [callsError, setCallsError] = useState<string | null>(null);
  const [callsPage, setCallsPage] = useState(1);

  const loadCalls = useCallback(() => {
    setCallsLoading(true);
    setCallsError(null);
    return getTranscriptCalls(filter)
      .then(rows => { setCalls(rows); setCallsPage(1); })
      .catch(() => setCallsError('Could not load matching calls — try again.'))
      .finally(() => setCallsLoading(false));
  }, [filter]);

  useEffect(() => { loadCalls(); }, [loadCalls]);

  // ── Admin-triggered fetch run + polling (FR-5.3/5.4, mirrors ZoomSweepPanel exactly) ───────
  const [run, setRun] = useState<TranscriptFetchRun | null>(null);
  const [runBusy, setRunBusy] = useState(false);
  // A 409 (a run IS already in progress, just not one this click started) is a calm heads-up —
  // rendered as plain text, not an alert/error banner — distinct from every other failure, which
  // gets role="alert" and red text. Tracked separately from the message itself so the render
  // doesn't have to re-sniff the error shape.
  const [runError, setRunError] = useState<string | null>(null);
  const [runErrorIsConflict, setRunErrorIsConflict] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollRunStatus = useCallback(() => {
    getTranscriptFetchStatus()
      .then(s => {
        setRun(s);
        if (!s || s.Status !== 'Running') {
          stopPolling();
          setRunBusy(false);
          // Fetch statuses may have just changed underneath the current filter — refresh both
          // the preview counts and the results list so they reflect the run that just finished.
          loadPreview();
          loadCalls();
        }
      })
      .catch(() => {
        // A transient poll failure shouldn't kill the in-progress indicator — just retry next tick.
      });
  }, [stopPolling, loadPreview, loadCalls]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollTimerRef.current = setInterval(pollRunStatus, POLL_MS);
  }, [stopPolling, pollRunStatus]);

  useEffect(() => stopPolling, [stopPolling]);

  // Resume visibility of an already-in-progress run on mount (e.g. the admin reloaded the page
  // mid-run) — a single one-time check, not scoped to the filter (the status endpoint tracks
  // the single most recent run, same as SweepRun).
  useEffect(() => {
    getTranscriptFetchStatus()
      .then(s => {
        setRun(s);
        if (s && s.Status === 'Running') {
          setRunBusy(true);
          startPolling();
        }
      })
      .catch(() => {
        // Non-fatal — the admin can still trigger a new run even if this initial check fails.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRunFetch = async () => {
    if (runBusy) return;
    setRunBusy(true);
    setRunError(null);
    setRunErrorIsConflict(false);
    try {
      await runTranscriptFetch(filter);
      // The trigger's 202 body is just { RunId } by contract — fetch full status immediately so
      // the Running state (and its counts) render right away, then poll to a terminal status.
      const status = await getTranscriptFetchStatus();
      setRun(status);
      if (status && status.Status === 'Running') {
        startPolling();
      } else {
        setRunBusy(false);
      }
    } catch (err) {
      const isConflict = isAxiosError(err) && err.response?.status === 409;
      setRunErrorIsConflict(isConflict);
      setRunError(extractErrorMessage(
        err,
        isConflict ? 'A transcript fetch is already in progress.' : 'Could not start the transcript fetch — try again.',
      ));
      // A 409 means a run IS already in progress (just not one this click started) — poll
      // anyway so the admin sees it through to completion, mirroring ZoomSweepPanel's handling.
      if (isConflict) {
        startPolling();
      } else {
        setRunBusy(false);
      }
    }
  };

  // ── Export (FR Group 6) ─────────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState<'json' | 'csv' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const handleExport = async (format: 'json' | 'csv') => {
    setExporting(format);
    setExportError(null);
    try {
      await exportTranscripts(filter, format);
    } catch {
      setExportError('Export failed — try again.');
    } finally {
      setExporting(null);
    }
  };

  // ── Viewer (spot-check, OQ-5 v1 scope) ──────────────────────────────────────────────────
  const [viewerCallId, setViewerCallId] = useState<number | null>(null);

  const categoryLabel = (id: number) => categories.find(c => c.CategoryID === id)?.Code ?? `#${id}`;

  const pagedCalls = calls.slice((callsPage - 1) * RESULTS_PAGE_SIZE, callsPage * RESULTS_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(calls.length / RESULTS_PAGE_SIZE));

  return (
    <section className={ui.sectionCard}>
      <h2 className={`${ui.sectionTitle} mb-1`}>Transcript Retrieval</h2>
      <p className="mb-4 text-sm text-muted">
        Fetches and stores Zoom call transcripts for a selected set of already-answered calls, for
        spot-checking quality and exporting a corpus. Transcripts may contain names, account
        numbers, and other family/student PII — this section is CommunicationAdmin-only.
      </p>

      {/* ── Filter ───────────────────────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <CategoryMultiSelect
          idPrefix="transcript-filter"
          categories={categories}
          selected={filter.categoryIds}
          onChange={ids => setFilter(f => ({ ...f, categoryIds: ids }))}
        />
        <div>
          <label className={ui.label} htmlFor="transcript-from">From</label>
          <input
            id="transcript-from"
            type="date"
            className={ui.control}
            value={filter.from}
            max={filter.to}
            onChange={e => setFilter(f => ({ ...f, from: e.target.value }))}
          />
        </div>
        <div>
          <label className={ui.label} htmlFor="transcript-to">To</label>
          <input
            id="transcript-to"
            type="date"
            className={ui.control}
            value={filter.to}
            min={filter.from}
            onChange={e => setFilter(f => ({ ...f, to: e.target.value }))}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-secondary" htmlFor="transcript-answered-only">
          <input
            id="transcript-answered-only"
            type="checkbox"
            className={ui.checkbox}
            checked={filter.answeredOnly}
            onChange={e => setFilter(f => ({ ...f, answeredOnly: e.target.checked }))}
          />
          Answered only
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-secondary" htmlFor="transcript-solo-category">
          <input
            id="transcript-solo-category"
            type="checkbox"
            className={ui.checkbox}
            checked={filter.soloCategoryOnly}
            onChange={e => setFilter(f => ({ ...f, soloCategoryOnly: e.target.checked }))}
          />
          Single category only
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-secondary" htmlFor="transcript-require-agent">
          <input
            id="transcript-require-agent"
            type="checkbox"
            className={ui.checkbox}
            checked={filter.requireAgent}
            onChange={e => setFilter(f => ({ ...f, requireAgent: e.target.checked }))}
          />
          Has an agent
        </label>
      </div>

      {/* ── Preview ──────────────────────────────────────────────────────────────────────── */}
      <div aria-live="polite" className="mb-6 text-sm">
        {previewLoading && <span className="text-muted">Loading preview…</span>}
        {!previewLoading && previewError && <span role="alert" className="text-red-600">{previewError}</span>}
        {!previewLoading && !previewError && preview && (
          <span className="text-secondary" data-testid="transcript-preview">
            <span className="font-medium text-primary">{preview.MatchingCalls}</span> matching calls &middot;{' '}
            <span className="font-medium text-primary">{preview.WithRecording}</span> with a recording &middot;{' '}
            <span className="font-medium text-primary">{preview.Fetched}</span> already fetched &middot;{' '}
            <span className="font-medium text-primary">{preview.Pending}</span> pending
          </span>
        )}
      </div>

      {/* ── Run + Export actions ─────────────────────────────────────────────────────────── */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button type="button" className={ui.btnPrimary} onClick={handleRunFetch} disabled={runBusy}>
          {runBusy ? 'Running…' : 'Run Transcript Fetch'}
        </button>
        <button
          type="button"
          className={ui.btnSecondary}
          onClick={() => handleExport('json')}
          disabled={exporting !== null}
        >
          {exporting === 'json' ? 'Exporting…' : 'Export JSON'}
        </button>
        <button
          type="button"
          className={ui.btnSecondary}
          onClick={() => handleExport('csv')}
          disabled={exporting !== null}
        >
          {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <div aria-live="polite" className="mb-6 space-y-2">
        {/* A 409 reads as a calm heads-up, not an error banner — a run IS in progress, it's just
            not the one this click started, and polling below will carry it to completion. */}
        {runError && runErrorIsConflict && (
          <p className="text-sm text-secondary">{runError}</p>
        )}
        {runError && !runErrorIsConflict && (
          <p role="alert" className="text-sm text-red-600">{runError}</p>
        )}
        {exportError && <p role="alert" className="text-sm text-red-600">{exportError}</p>}
        {run && <TranscriptRunSummary run={run} />}
      </div>

      {/* ── Results list ─────────────────────────────────────────────────────────────────── */}
      <h3 className="mb-2 text-sm font-semibold text-secondary">Matching Calls</h3>
      {callsLoading && <LoadingSpinner size="sm" message="Loading matching calls…" />}
      {!callsLoading && callsError && <p role="alert" className="text-sm text-red-600">{callsError}</p>}
      {!callsLoading && !callsError && (
        <>
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead className={ui.thead}>
                <tr>
                  <th className={ui.th}>Time (ET)</th>
                  <th className={ui.th}>Agent</th>
                  <th className={ui.th}>Duration</th>
                  <th className={ui.th}>Categories</th>
                  <th className={ui.th}>Status</th>
                  <th className={ui.th}>Segments</th>
                  <th className={`${ui.th} text-right`}>&nbsp;</th>
                </tr>
              </thead>
              <tbody className={ui.tbody}>
                {pagedCalls.map(c => (
                  <tr key={c.CallID} className="hover:bg-surface-hover">
                    <td className={`${ui.td} whitespace-nowrap`}>{formatDateTimeET(c.CallTimeUtc)}</td>
                    <td className={ui.td}>{c.AgentName ?? (c.AgentID ? `#${c.AgentID}` : <span className="text-muted">—</span>)}</td>
                    <td className={ui.td}>{formatDurationSeconds(c.DurationSeconds)}</td>
                    <td className={ui.td}>{c.CategoryIDs.map(categoryLabel).join(', ') || <span className="text-muted">—</span>}</td>
                    <td className={ui.td}><StatusBadge status={c.FetchStatus} /></td>
                    <td className={ui.td}>{c.SegmentCount ?? <span className="text-muted">—</span>}</td>
                    <td className={`${ui.td} text-right`}>
                      <button type="button" className={ui.btnSecondary} onClick={() => setViewerCallId(c.CallID)}>
                        {c.FetchStatus === 'Fetched' ? 'View transcript' : 'View details'}
                      </button>
                    </td>
                  </tr>
                ))}
                {calls.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">No calls match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {calls.length > RESULTS_PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-between text-sm text-muted">
              <span>
                {(callsPage - 1) * RESULTS_PAGE_SIZE + 1}
                &ndash;{Math.min(callsPage * RESULTS_PAGE_SIZE, calls.length)} of {calls.length}
              </span>
              <div className="flex items-center gap-2">
                <button className={ui.btnSecondary} disabled={callsPage <= 1} onClick={() => setCallsPage(p => p - 1)}>Prev</button>
                <span>Page {callsPage} of {totalPages}</span>
                <button className={ui.btnSecondary} disabled={callsPage >= totalPages} onClick={() => setCallsPage(p => p + 1)}>Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {viewerCallId !== null && (
        <TranscriptViewer callId={viewerCallId} categories={categories} onClose={() => setViewerCallId(null)} />
      )}
    </section>
  );
};
